// src/modules/compass/persistence.js
//
// M2.1 persistence for Compass, against the intel schema.
//
// Conversation messages are append-only. There is deliberately no update and
// no delete function for them here, and the database grants no such privilege
// — intel.conversation_messages has a SELECT policy and an INSERT policy and
// nothing else. Evidence is permanent; understanding is what gets revised.
//
// ── SCHEMA ACCESS PATTERN — read before adding any call ─────────────────────
// The intel schema is NOT public. Every call must go through the schema
// accessor:
//
//     supabase.schema('intel').from('sessions')
//
// and NEVER:
//
//     supabase.from('intel.sessions')
//
// The second form does not select a schema. PostgREST reads it as a table in
// the exposed (public) schema whose name is literally the string
// "intel.sessions", finds nothing, and fails with a confusing "relation does
// not exist" that looks like a missing migration rather than a client bug.
// ───────────────────────────────────────────────────────────────────────────

import { supabase } from '../../supabase';

const INTEL = () => supabase.schema('intel');

// Mirrors the CHECK constraint on intel.conversation_messages.role.
const MESSAGE_ROLES = ['user', 'assistant'];

// Mirrors the CHECK constraint on intel.understanding_units.kind. Validated
// in JS so a bad value fails with a readable error at the call site instead
// of surfacing as a generic 23514 constraint violation from Postgres.
const UNIT_KINDS = ['observation', 'decision', 'goal', 'priority', 'note', 'next_step'];

// ── Sessions ──────────────────────────────────────────────────

export async function listSessions(subjectId) {
  const { data, error } = await INTEL()
    .from('sessions')
    .select('*')
    .eq('subject_id', subjectId)
    .order('started_at', { ascending: false });
  if (error) throw error;
  return data;
}

// created_by defaults to auth.uid() in the database and is enforced by the
// sessions_insert policy (created_by = auth.uid()). Application code must not
// set it — authorship is established by the session's JWT, not by the browser.
export async function createSession(subjectId) {
  const { data, error } = await INTEL()
    .from('sessions')
    .insert({ subject_id: subjectId })
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ── Conversation messages (append-only) ───────────────────────

// Ordered by seq, never by created_at. seq is the identity column: it is
// monotonic, gap-tolerant, and assigned by the database at insert, so it is
// the only ordering guarantee. Two messages can share a created_at timestamp
// at timestamptz resolution, which would make conversation order
// nondeterministic.
export async function listMessages(subjectId) {
  const { data, error } = await INTEL()
    .from('conversation_messages')
    .select('*')
    .eq('subject_id', subjectId)
    .order('seq', { ascending: true });
  if (error) throw error;
  return data;
}

export async function appendMessage(subjectId, { sessionId, role, content }) {
  if (!MESSAGE_ROLES.includes(role)) {
    throw new Error(
      `appendMessage: invalid role "${role}". Must be one of: ${MESSAGE_ROLES.join(', ')}.`
    );
  }

  const { data, error } = await INTEL()
    .from('conversation_messages')
    .insert({
      subject_id: subjectId,
      session_id: sessionId ?? null,
      role,
      content,
    })
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ── Understanding units ───────────────────────────────────────

// Returns every status — 'proposed', 'confirmed', and 'retracted'. Filtering
// is the caller's concern: the review surface and the domain-state derivation
// want different subsets, and neither should be baked in here.
export async function listUnits(subjectId) {
  const { data, error } = await INTEL()
    .from('understanding_units')
    .select('*')
    .eq('subject_id', subjectId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return data;
}

export async function createUnit(
  subjectId,
  { kind, domainKey, statement, detail, sessionId, sourceMessageId }
) {
  if (!UNIT_KINDS.includes(kind)) {
    throw new Error(
      `createUnit: invalid kind "${kind}". Must be one of: ${UNIT_KINDS.join(', ')}.`
    );
  }

  // source and status are fixed for M2.1. Every unit written in this
  // increment is authored by an advisor and is believed on write.
  // Writing source: 'compass' with status: 'proposed' — understanding the
  // system proposes and a human confirms — is a later, separately scoped
  // increment. It is not a flag to flip here; it needs its own review
  // surface and its own confirmation path.
  //
  // domainKey passes through exactly as given, including null. This is
  // intentional and permanent: the database column is unconstrained by
  // design, domains are a rendering concern owned by src/config/domains.js,
  // and a unit with no domain is still valid understanding. No validation
  // may ever be added here — doing so would reintroduce, in application
  // code, the coupling the schema deliberately refuses.
  //
  // created_by defaults to auth.uid() in the database and is enforced by the
  // units_insert policy. Do not set it here.
  const { data, error } = await INTEL()
    .from('understanding_units')
    .insert({
      subject_id: subjectId,
      session_id: sessionId ?? null,
      kind,
      domain_key: domainKey ?? null,
      statement,
      detail: detail ?? null,
      source: 'advisor',
      status: 'confirmed',
      source_message_id: sourceMessageId ?? null,
    })
    .select()
    .single();
  if (error) throw error;
  return data;
}

// Sorting a unit into a domain, or back out of one. domainKey may be null —
// unsorting is a legitimate operation — and is not validated, for the same
// reason it is not validated in createUnit.
export async function assignDomain(unitId, domainKey) {
  const { data, error } = await INTEL()
    .from('understanding_units')
    .update({ domain_key: domainKey ?? null })
    .eq('id', unitId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ── Supersession: deliberately absent ─────────────────────────
//
// There is no supersedeUnit function in M2.1, and no provisional helper that
// approximates one. This is a design decision, not an omission.
//
// Superseding a belief means two operations: insert the replacement unit,
// then mark the prior unit superseded (status + superseded_by). It MUST NOT
// be implemented as two separate browser calls.
//
// The browser holds no DELETE privilege on these tables by design — the
// grants are SELECT, INSERT, UPDATE only. So if the first call succeeds and
// the second fails, the browser cannot compensate. It cannot remove the row
// it just wrote.
//
// The residue of that partial failure is two confirmed units asserting
// competing beliefs, with nothing in the data distinguishing the intended
// replacement from the belief it was meant to replace. That is silent,
// permanent ambiguity in the understanding record — the one failure mode
// this schema exists to prevent.
//
// The correct implementation is a single transactional database RPC that
// performs both writes or neither. Tracked as D-14.
